import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import LoadFailed from '../shared/load_failed';
import TpDialog from '../shared/tp_dialog';
import { discard_guard_text } from '../shared/dialog_logic';
import { fetch_email_text, save_email_text } from '../api';
import { email_error_view, field_errors_view, text_view } from './adapters';
import { blank_text_form, has_text_errors, text_form_errors } from './params';
import { save_text_body } from './requests';

/*
 * the editable greeting/intro/footer, per month with a "Save as default" choice. A reason is
 * required to save.
 */
const LOAD_FAILED_TEXT = 'Could not load the email text.';

const Field = ({ id, label, value, on_change, error, hint, textarea, max_len }) => {
  const Tag = textarea ? 'textarea' : 'input';
  return (
    <div className="tp-field">
      <label htmlFor={id}>{label}</label>
      <Tag
        id={id}
        className={textarea ? 'tp-textarea' : 'tp-input'}
        value={value}
        onChange={(e) => on_change(e.target.value)}
      />
      <span className="tp-hint">
        {Array.from(String(value || '')).length}/{max_len}
        {hint ? ` · ${hint}` : ''}
      </span>
      {error ? (
        <span className="tp-err" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
};

const TextEditorDialog = ({ pay_month, month_label, on_close, on_focus_lost, on_error, on_saved }) => {
  const [state, setState] = useState('loading'); // loading | ready | error
  const [error_text, setErrorText] = useState('');
  const [is_default, setIsDefault] = useState(false);
  const [form, setForm] = useState(blank_text_form());
  const [placeholders, setPlaceholders] = useState([]);
  const [touched, setTouched] = useState(false);
  const [server_errors, setServerErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [save_error, setSaveError] = useState('');
  const seq = useRef(0);
  const on_close_ref = useRef(on_close);
  on_close_ref.current = on_close;
  const on_error_ref = useRef(on_error);
  on_error_ref.current = on_error;

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setState('loading');
    fetch_email_text(pay_month)
      .then((data) => {
        if (mine !== seq.current) return;
        const view = text_view(data);
        setForm({ greeting: view.greeting, intro: view.intro, footer: view.footer, reason: '' });
        setIsDefault(view.is_default);
        setPlaceholders(view.placeholders);
        setState('ready');
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        const v = email_error_view(e, LOAD_FAILED_TEXT);
        if (v.kind === 'permission') {
          on_error_ref.current(e, LOAD_FAILED_TEXT);
          on_close_ref.current();
          return;
        }
        setErrorText(v.text);
        setState('error');
      });
  }, [pay_month]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const client_errors = text_form_errors(form);

  const submit = () => {
    setTouched(true);
    setServerErrors({});
    if (has_text_errors(client_errors)) return;
    setSaving(true);
    setSaveError('');
    const client_request_id = `email-text-${pay_month}-${Date.now()}`;
    save_email_text(save_text_body(pay_month, is_default, form, client_request_id))
      .then(() => {
        setSaving(false);
        if (on_saved) on_saved();
        on_close();
      })
      .catch((e) => {
        const v = email_error_view(e, 'Could not save the email text.');
        if (v.kind === 'permission') {
          setSaving(false);
          on_error(e, 'You do not have access to edit the email text.');
          return;
        }
        setSaving(false);
        setServerErrors(field_errors_view(e));
        setSaveError(v.text);
      });
  };

  const guard_text = discard_guard_text(form.reason);
  const field_error = (key) => (touched && client_errors[key]) || server_errors[key];
  const placeholder_hint = placeholders.length ? `Placeholders: ${placeholders.join(', ')}` : '';

  return (
    <TpDialog
      title={`Email text · ${month_label}`}
      busy={saving}
      guard_text={guard_text}
      on_close={on_close}
      on_focus_lost={on_focus_lost}
    >
      <div className="tp-em-text">
        {state === 'loading' ? <CustomLoading /> : null}
        {state === 'error' ? <LoadFailed text={error_text} on_retry={load} /> : null}
        {state === 'ready' ? (
          <>
            <label className="tp-em-check">
              <input
                type="checkbox"
                checked={is_default}
                onChange={(e) => setIsDefault(e.target.checked)}
              />
              Save as default for future months
            </label>
            <Field
              id="tp-em-greeting"
              label="Greeting"
              value={form.greeting}
              on_change={(v) => set('greeting', v)}
              error={field_error('greeting')}
              hint={placeholder_hint}
              max_len={256}
            />
            <Field
              id="tp-em-intro"
              label="Intro"
              value={form.intro}
              on_change={(v) => set('intro', v)}
              error={field_error('intro')}
              textarea
              max_len={2000}
            />
            <Field
              id="tp-em-footer"
              label="Footer"
              value={form.footer}
              on_change={(v) => set('footer', v)}
              error={field_error('footer')}
              textarea
              max_len={2000}
            />
            <Field
              id="tp-em-reason"
              label="Reason (required)"
              value={form.reason}
              on_change={(v) => set('reason', v)}
              error={field_error('reason')}
              textarea
              max_len={1024}
            />
            {save_error ? (
              <div className="tp-banner tp-banner-error" role="alert">
                {save_error}
              </div>
            ) : null}
            <div className="tp-modal-ft">
              <button
                type="button"
                className="tp-btn-ghost"
                onClick={saving ? undefined : on_close}
                aria-disabled={saving ? 'true' : undefined}
              >
                Cancel
              </button>
              <button
                type="button"
                className="tp-btn"
                onClick={saving ? undefined : submit}
                aria-disabled={saving ? 'true' : undefined}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </TpDialog>
  );
};

export default TextEditorDialog;
